<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";

import Button from "primevue/button";
import Card from "primevue/card";
import { useAppToast } from "@/shared/lib/useAppToast";

import ProductFormDialog from "@/modules/admin/products/products/components/ProductFormDialog.vue";
import DeleteProductConfirmDialog from "@/modules/admin/products/products/components/DeleteProductConfirmDialog.vue";
import ProductVariantsPanel from "@/modules/admin/products/products/components/ProductVariantsPanel.vue";

import { getResolvedProductById } from "@/modules/catalog/api/catalog.service";
import {
  updateProduct,
  deleteProduct as deleteProductApi,
} from "@/modules/admin/products/products/api/products.service";
import { getAllProductTypes } from "@/modules/admin/products/product-types/api/productTypes.service";

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/
function isHexColor(str) {
  return HEX_RE.test(str?.trim() ?? '')
}
function isColorAttr(attr) {
  return attr.name?.toLowerCase().includes('цвет')
}

const route = useRoute();
const router = useRouter();
const toast = useAppToast();

const product = ref(null);
const productTypes = ref([]);
const loading = ref(false);
const selectedImage = ref(null);

const formVisible = ref(false);
const selectedProduct = ref(null);

const deleteDialogVisible = ref(false);

const submitting = ref(false);
const deleting = ref(false);

async function loadProduct() {
  try {
    loading.value = true;

    const rawId = route.params.id;
    const productId = Number(rawId);

    if (!Number.isInteger(productId) || productId <= 0) {
      product.value = null;
      toast.error("Некорректный идентификатор товара");
      return;
    }

    const rawProduct = await getResolvedProductById(productId);

    if (rawProduct.attributes && Array.isArray(rawProduct.attributes)) {
      rawProduct.attributes.forEach((attr) => {
        const selectedId = attr.selectedValueId ?? undefined;
        const selected = attr.values?.find((v) => v.id === selectedId);
        attr.selectedValue = selected ? selected.value : "Не выбрано";
      });
    }

    product.value = rawProduct;

    selectedImage.value =
      product.value?.images?.find((img) => img.isDefault)?.url ||
      product.value?.images?.[0]?.url ||
      null;
  } catch (error) {
    product.value = null;
    toast.error(error.message || "Ошибка загрузки товара");
  } finally {
    loading.value = false;
  }
}

async function loadProductTypes() {
  try {
    productTypes.value = await getAllProductTypes();
  } catch (error) {
    productTypes.value = [];
    toast.error(error.message || "Ошибка загрузки типов товаров");
  }
}

function getActualPrice() {
  const salePriceKzt = Number(product.value?.salePriceKzt)
  const basePriceKzt = Number(product.value?.basePriceKzt)

  if (product.value?.saleIsActive && !Number.isNaN(salePriceKzt)) {
    return salePriceKzt
  }

  return Number.isNaN(basePriceKzt) ? 0 : basePriceKzt
}

function selectImage(image) {
  selectedImage.value = image.url;
}

function goBack() {
  router.push("/admin/products");
}

function openEditDialog() {
  if (!product.value?.id) return;

  router.push({
    name: "adminProductEdit",
    params: { id: String(product.value.id) },
  });
}

function closeFormDialog() {
  if (submitting.value) return;
  formVisible.value = false;
  selectedProduct.value = null;
}

async function handleSubmitProduct(formData) {
  try {
    if (!selectedProduct.value?.id) return;

    submitting.value = true;

    let payload = {
      articul: formData.articul,
      basePrice: formData.basePrice,
      salePrice: formData.salePrice,
      saleIsActive: formData.saleIsActive,
      quantity: formData.quantity,
      productTypeId:
        formData.productTypeId ?? selectedProduct.value.productTypeId,
      images: (formData.images ?? []).map((img) =>
        img.id
          ? { id: img.id, url: img.storageKey || img.url, isDefault: !!img.isDefault }
          : { url: img.storageKey || img.url, isDefault: !!img.isDefault },
      ),
      characteristicValues: (
        formData.characteristicValues ??
        formData.variants ??
        []
      )
        .filter((v) => v.valueId)
        .map((v) => ({ valueId: Number(v.valueId) })),
    };

    if (
      !payload.images.some((img) => img.isDefault) &&
      payload.images.length > 0
    ) {
      payload.images[0].isDefault = true;
    } else if (payload.images.filter((img) => img.isDefault).length > 1) {
      let firstDefault = true;
      payload.images = payload.images.map((img) => {
        if (img.isDefault) {
          if (firstDefault) {
            firstDefault = false;
            return img;
          }
          return { ...img, isDefault: false };
        }
        return img;
      });
    }

    await updateProduct(selectedProduct.value.id, payload);

    toast.success("Товар обновлён");
    closeFormDialog();
    await loadProduct();
  } catch (error) {
    toast.error(
      error.response?.data?.message ?? "Ошибка при сохранении товара",
    );
  } finally {
    submitting.value = false;
  }
}

function openDeleteDialog() {
  deleteDialogVisible.value = true;
}

function closeDeleteDialog() {
  if (deleting.value) return;
  deleteDialogVisible.value = false;
}

async function confirmDeleteProduct() {
  if (deleting.value) return
  try {
    if (!product.value?.id) return;

    deleting.value = true;

    await deleteProductApi(product.value.id);
    toast.success("Товар удалён");

    router.push("/admin/products");
  } catch (error) {
    toast.error("Не удалось удалить товар");
  } finally {
    deleting.value = false
    closeDeleteDialog();
  }
}

onMounted(async () => {
  await Promise.all([loadProduct(), loadProductTypes()]);
});
</script>

<template>
  <div class="admin-product-page">
    <div class="top-bar">
      <Button
        label="Назад"
        icon="pi pi-arrow-left"
        severity="secondary"
        @click="goBack"
      />
      <div class="top-actions" v-if="product">
        <Button
          label="Редактировать"
          icon="pi pi-pencil"
          severity="secondary"
          outlined
          @click="openEditDialog"
        />
        <Button
          label="Удалить"
          icon="pi pi-trash"
          severity="danger"
          outlined
          @click="openDeleteDialog"
        />
      </div>
    </div>

    <div v-if="loading" class="state-block">Загрузка товара...</div>
    <div v-else-if="!product" class="state-block">Товар не найден</div>

    <Card v-else>
      <template #title>Детали товара</template>
      <template #content>
        <div class="product-layout">
          <div class="images-section">
            <div class="main-image">
              <img
                v-if="selectedImage"
                :src="selectedImage"
                :alt="product.name"
              />
              <div v-else class="image-placeholder">Нет изображения</div>
            </div>

            <div
              v-if="product.images && product.images.length > 1"
              class="images-preview"
            >
              <img
                v-for="img in product.images"
                :key="img.id"
                :src="img.url"
                :alt="product.name"
                class="preview-image"
                :class="{ selected: selectedImage === img.url }"
                @click="selectImage(img)"
              />
            </div>
          </div>

          <div class="info-section">
            <div class="brand">{{ product.brandName }}</div>
            <h1 class="product-title">{{ product.name }}</h1>

            <div class="meta-row">
              <span>Артикул: {{ product.articul }}</span>
              <span>Категория: {{ product.categoryName }}</span>
            </div>

            <div class="meta-row">
              <span>Остаток: {{ product.quantity }}</span>
              <span
                >Статус: {{ product.isActive ? "Активен" : "Неактивен" }}</span
              >
            </div>

            <p class="description">
              {{ product.description || "Описание отсутствует" }}
            </p>

            <div class="price-block">
              <span
                v-if="product.saleIsActive && product.salePriceKzt != null"
                class="old-price"
              >
                {{ product.basePriceKzt }} ₸
              </span>

              <span class="actual-price">
                {{ getActualPrice() }} ₸
              </span>

              <span class="price-eur">
                {{
                  product.saleIsActive && product.salePrice != null
                  ? product.salePrice
                  : product.basePrice
                }}
                  €
              </span>
            </div>

            <div class="product-type-block">
              <div class="section-title">Основной товар</div>
              <div>ID: {{ product.productTypeId }}</div>
            </div>

            <div
              v-if="product.attributes && product.attributes.length"
              class="attributes"
            >
              <div class="section-title">Характеристики</div>

              <div
                v-for="attr in product.attributes"
                :key="attr.id"
                class="attribute-row"
              >
                <span class="attr-name">{{ attr.name }}</span>
                <span class="attr-value">
                  <span
                    v-if="isColorAttr(attr) && isHexColor(attr.selectedValue)"
                    class="color-swatch"
                    :style="{ background: attr.selectedValue.trim() }"
                  />
                  {{ attr.selectedValue }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Card>
    <ProductVariantsPanel
      v-if="product?.id"
      :product-id="product.id"
    />

    <ProductFormDialog 
      :visible="formVisible" 
      mode="edit"
      :product="selectedProduct" 
      :product-types="productTypes"
      @close="closeFormDialog"
      @submit="handleSubmitProduct"
    />

    <DeleteProductConfirmDialog
      :visible="deleteDialogVisible"
      :product="product"
      :deleting="deleting"
      @close="closeDeleteDialog"
      @confirm="confirmDeleteProduct"
    />
  </div>
</template>

<style scoped>
.admin-product-page {
  max-width: 1200px;
  margin: auto;
  padding: 24px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.top-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.state-block {
  text-align: center;
  padding: 40px;
  color: #6b7280;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
}

.product-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
}

.images-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.main-image {
  width: 100%;
  height: 420px;
  background: #f3f4f6;
  border-radius: 12px;
  overflow: hidden;
}

.main-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.images-preview {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.preview-image {
  width: 84px;
  height: 84px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  background: #f3f4f6;
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.brand {
  font-size: 14px;
  color: #6b7280;
}

.product-title {
  margin: 0;
  font-size: 32px;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #6b7280;
}

.description {
  line-height: 1.6;
  margin: 0;
}

.price-block {
  display: flex;
  gap: 12px;
  align-items: center;
}

.old-price {
  text-decoration: line-through;
  color: #9ca3af;
}

.actual-price {
  font-size: 28px;
  font-weight: 700;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.product-type-block {
  margin-top: 8px;
}

.attributes {
  margin-top: 8px;
}

.attribute-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

.attr-name {
  color: #6b7280;
}

.attr-value {
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.color-swatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .product-layout {
    grid-template-columns: 1fr;
  }

  .top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .top-actions {
    justify-content: flex-end;
  }
}

@media (max-width: 640px) {
  .admin-product-page {
    padding: 16px;
  }

  .top-actions {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .product-title {
    font-size: 24px;
  }

  .actual-price {
    font-size: 22px;
  }

  .main-image {
    height: 280px;
  }

  .preview-image {
    width: 64px;
    height: 64px;
  }

  .meta-row {
    flex-direction: column;
    gap: 6px;
  }

  .attribute-row {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
